import { FieldMergeFunction } from '@apollo/client/cache/inmemory/policies';
import type {
  FieldPolicy,
  FieldReadFunction,
  KeyFieldsFunction,
} from '@apollo/client/cache/inmemory/policies';
import type { Mutation, Query } from '../schema.graphql';
import type { GqlTypeMap } from '../typeMap';
import { handleDelete } from './deletes/handleDelete';
import { redirectToTypeById } from './id-lookup/field-policy';
import { pageLimitPagination } from './lists/page-limit-pagination';
import { optional, Parsers } from './scalars/scalars.parser';

type FieldPolicies<T> = {
  [K in keyof T]?: FieldPolicy<T[K]> | FieldReadFunction<T[K]>;
};

type KeySpecifier<K = string> = ReadonlyArray<K | KeySpecifier>;

type GqlTypeMapAndQueries = GqlTypeMap & { Query: Query; Mutation: Mutation };

export interface TypePolicy<T> {
  keyFields?: KeySpecifier<keyof T> | KeyFieldsFunction | false;
  merge?: FieldMergeFunction<T> | boolean;
  queryType?: true;
  mutationType?: true;
  subscriptionType?: true;
  fields?: FieldPolicies<T>;
}

type TypePolicies = {
  [K in keyof GqlTypeMapAndQueries]?: TypePolicy<GqlTypeMapAndQueries[K]>;
};

export const typePolicies: TypePolicies = {
  DateRange: {
    keyFields: false,
    merge: true,
    fields: {
      end: {
        read: optional(Parsers.Date),
      },
      start: {
        read: optional(Parsers.Date),
      },
    },
  },
  Language: {
    fields: {
      ethnologue: { merge: true },
      locations: {},
      projects: {},
      createdAt: {
        read: Parsers.DateTime,
      },
      commentThreads: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      engagements: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      history: pageLimitPagination(),
      posts: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      tools: pageLimitPagination(),
    },
  },
  EthnologueLanguage: { keyFields: false },
  IanaCountry: { keyFields: ['code'] },
  TimeZone: { keyFields: ['name'] },
  IsoCountry: { keyFields: ['alpha3'] },
  Secured: {
    keyFields: false,
    merge: true,
  },
  FileNode: {
    fields: {
      parents: {
        merge: false,
      },
    },
  },
  ChangesetAware: {
    // Include changeset ID in ChangesetAware's key fields if it is given
    keyFields: (object: any) =>
      object?.changeset?.id ? ['id', 'changeset', ['id']] : ['id'],
  },
  ChangesetDiff: {
    merge: true,
  },
  ProductProgress: {
    keyFields: ['product', ['id'], 'report', ['id'], 'variant', ['key']],
  },
  LanguageEngagement: {
    fields: {
      partnershipsProducingMediums: {
        // Entire updated list is returned each time.
        merge: false,
      },
      createdAt: {
        read: Parsers.DateTime,
      },
      modifiedAt: {
        read: Parsers.DateTime,
      },
      commentThreads: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      history: pageLimitPagination(),
      products: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      progressReports: pageLimitPagination({ sort: 'start', order: 'ASC' }),
      tools: pageLimitPagination(),
    },
  },
  ProgressReport: {
    fields: {
      varianceExplanation: {
        merge: true,
      },
      createdAt: {
        read: Parsers.DateTime,
      },
      due: {
        read: Parsers.Date,
      },
      end: {
        read: Parsers.Date,
      },
      start: {
        read: Parsers.Date,
      },
      commentThreads: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      communityStories: pageLimitPagination(),
      highlights: pageLimitPagination(),
      history: pageLimitPagination(),
      media: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      teamNews: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  FieldRegion: {
    fields: {
      projects: {}, // no page merging (infinite scroll)
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  FieldZone: {
    fields: {
      projects: {}, // no page merging (infinite scroll)
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  Partner: {
    fields: {
      projects: {}, // no page merging (infinite scroll)
      engagements: {},
      createdAt: {
        read: Parsers.DateTime,
      },
      modifiedAt: {
        read: Parsers.DateTime,
      },
      commentThreads: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      history: pageLimitPagination(),
      languages: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      people: pageLimitPagination({ sort: 'fullName', order: 'ASC' }),
      posts: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      tools: pageLimitPagination(),
    },
  },
  User: {
    fields: {
      projects: {}, // no page merging (infinite scroll)
      partners: {}, // no page merging (infinite scroll)
      createdAt: {
        read: Parsers.DateTime,
      },
      commentThreads: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      education: pageLimitPagination({ sort: 'institution', order: 'ASC' }),
      history: pageLimitPagination(),
      locations: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      organizations: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      tools: pageLimitPagination(),
      unavailabilities: pageLimitPagination({ sort: 'start', order: 'DESC' }),
    },
  },
  Query: {
    fields: {
      projects: {},
      engagements: {},
      progressReports: {},
      partners: {},
      users: {},
      languages: {},
      unstable_stubDate: {
        read: optional(Parsers.Date),
      },
      unstable_stubDateTime: {
        read: optional(Parsers.DateTime),
      },
      unstable_stubDuration: {
        read: optional(Parsers.Duration),
      },
      unstable_stubInlineMarkdown: {
        read: optional(Parsers.InlineMarkdown),
      },
      unstable_stubJMESPath: {
        read: optional(Parsers.JMESPath),
      },
      unstable_stubMarkdown: {
        read: optional(Parsers.Markdown),
      },
      unstable_stubRichText: {
        read: optional(Parsers.RichText),
      },
      unstable_stubURL: {
        read: optional(Parsers.URL),
      },
      unstable_stubUpload: {
        read: optional(Parsers.Upload),
      },
      commentThreads: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      educations: pageLimitPagination({ sort: 'institution', order: 'ASC' }),
      ethnoArts: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      fieldRegions: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      fieldZones: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      films: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      fundingAccounts: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      internshipEngagements: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      internshipProjects: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      languageEngagements: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      locations: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      notifications: pageLimitPagination(),
      organizations: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      partnerships: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      periodicReports: pageLimitPagination({ sort: 'start', order: 'ASC' }),
      products: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      stories: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      suggestProductCompletionDescriptions: pageLimitPagination(),
      tools: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      translationProjects: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      unavailabilities: pageLimitPagination({ sort: 'start', order: 'DESC' }),
      webhooks: pageLimitPagination(),
      changeset: redirectToTypeById('Changeset'),
      commentThread: redirectToTypeById('CommentThread'),
      directory: redirectToTypeById('Directory'),
      education: redirectToTypeById('Education'),
      engagement: redirectToTypeById('Engagement'),
      ethnoArt: redirectToTypeById('EthnoArt'),
      fieldRegion: redirectToTypeById('FieldRegion'),
      fieldZone: redirectToTypeById('FieldZone'),
      file: redirectToTypeById('File'),
      fileNode: redirectToTypeById('FileNode'),
      film: redirectToTypeById('Film'),
      fundingAccount: redirectToTypeById('FundingAccount'),
      internshipEngagement: redirectToTypeById('InternshipEngagement'),
      language: redirectToTypeById('Language'),
      languageEngagement: redirectToTypeById('LanguageEngagement'),
      location: redirectToTypeById('Location'),
      organization: redirectToTypeById('Organization'),
      partner: redirectToTypeById('Partner'),
      partnership: redirectToTypeById('Partnership'),
      periodicReport: redirectToTypeById('PeriodicReport'),
      post: redirectToTypeById('Post'),
      product: redirectToTypeById('Product'),
      project: redirectToTypeById('Project'),
      story: redirectToTypeById('Story'),
      tool: redirectToTypeById('Tool'),
      translationProject: redirectToTypeById('TranslationProject'),
      unavailability: redirectToTypeById('Unavailability'),
      user: redirectToTypeById('User'),
    },
  },
  Audio: {
    fields: {
      url: {
        read: Parsers.URL,
      },
    },
  },
  Budget: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  BudgetRecord: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  Ceremony: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  CeremonyCreated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  CeremonyDeleted: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  CeremonyUpdate: {
    fields: {
      actualDate: {
        read: optional(Parsers.Date),
      },
      estimatedDate: {
        read: optional(Parsers.Date),
      },
    },
  },
  CeremonyUpdated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  Comment: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      modifiedAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  CommentThread: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      comments: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  CommentViaMentionNotification: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      readAt: {
        read: optional(Parsers.DateTime),
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  DerivativeScriptureProduct: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  DerivativeScriptureProductCreated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  DerivativeScriptureProductDeleted: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  DerivativeScriptureProductUpdated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  DirectScriptureProduct: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  DirectScriptureProductCreated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  DirectScriptureProductDeleted: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  DirectScriptureProductUpdated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  Directory: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      modifiedAt: {
        read: Parsers.DateTime,
      },
      children: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  Education: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  EthnoArt: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  File: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      modifiedAt: {
        read: Parsers.DateTime,
      },
      url: {
        read: Parsers.URL,
      },
      children: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  FileVersion: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      url: {
        read: Parsers.URL,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  Film: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  FinancialReport: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      due: {
        read: Parsers.Date,
      },
      end: {
        read: Parsers.Date,
      },
      start: {
        read: Parsers.Date,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  FundingAccount: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  Image: {
    fields: {
      url: {
        read: Parsers.URL,
      },
    },
  },
  InternshipEngagement: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      modifiedAt: {
        read: Parsers.DateTime,
      },
      commentThreads: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  InternshipEngagementCreated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  InternshipEngagementDeleted: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  InternshipEngagementUpdate: {
    fields: {
      completeDate: {
        read: optional(Parsers.Date),
      },
      description: {
        read: optional(Parsers.RichText),
      },
      disbursementCompleteDate: {
        read: optional(Parsers.Date),
      },
      endDateOverride: {
        read: optional(Parsers.Date),
      },
      startDateOverride: {
        read: optional(Parsers.Date),
      },
    },
  },
  InternshipEngagementUpdated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  InternshipProject: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      modifiedAt: {
        read: Parsers.DateTime,
      },
      changeRequests: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      commentThreads: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      engagements: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      financialReports: pageLimitPagination({ sort: 'start', order: 'ASC' }),
      history: pageLimitPagination(),
      internshipEngagements: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      narrativeReports: pageLimitPagination({ sort: 'start', order: 'ASC' }),
      otherLocations: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      partnerships: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      posts: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      team: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      tools: pageLimitPagination(),
    },
  },
  LanguageCreated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  LanguageDeleted: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  LanguageEngagementCreated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  LanguageEngagementDeleted: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  LanguageEngagementUpdate: {
    fields: {
      completeDate: {
        read: optional(Parsers.Date),
      },
      description: {
        read: optional(Parsers.RichText),
      },
      disbursementCompleteDate: {
        read: optional(Parsers.Date),
      },
      endDateOverride: {
        read: optional(Parsers.Date),
      },
      startDateOverride: {
        read: optional(Parsers.Date),
      },
    },
  },
  LanguageEngagementUpdated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  LanguageUpdate: {
    fields: {
      sponsorEstimatedEndDate: {
        read: optional(Parsers.Date),
      },
    },
  },
  LanguageUpdated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  Location: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  MomentumTranslationProject: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      modifiedAt: {
        read: Parsers.DateTime,
      },
      changeRequests: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      commentThreads: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      engagements: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      financialReports: pageLimitPagination({ sort: 'start', order: 'ASC' }),
      history: pageLimitPagination(),
      languageEngagements: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      narrativeReports: pageLimitPagination({ sort: 'start', order: 'ASC' }),
      otherLocations: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      partnerships: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      posts: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      team: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      tools: pageLimitPagination(),
    },
  },
  MultiplicationTranslationProject: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      modifiedAt: {
        read: Parsers.DateTime,
      },
      changeRequests: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      commentThreads: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      engagements: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      financialReports: pageLimitPagination({ sort: 'start', order: 'ASC' }),
      history: pageLimitPagination(),
      languageEngagements: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      narrativeReports: pageLimitPagination({ sort: 'start', order: 'ASC' }),
      otherLocations: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      partnerships: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      posts: pageLimitPagination({ sort: 'createdAt', order: 'DESC' }),
      team: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      tools: pageLimitPagination(),
    },
  },
  NarrativeReport: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      due: {
        read: Parsers.Date,
      },
      end: {
        read: Parsers.Date,
      },
      start: {
        read: Parsers.Date,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  Organization: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      locations: pageLimitPagination({ sort: 'name', order: 'ASC' }),
      tools: pageLimitPagination(),
    },
  },
  OtherProduct: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  OtherProductCreated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  OtherProductDeleted: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  OtherProductUpdated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  Partnership: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  PnpProblem: {
    fields: {
      documentation: {
        read: optional(Parsers.URL),
      },
      message: {
        read: Parsers.InlineMarkdown,
      },
    },
  },
  Post: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      modifiedAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  ProgressReportMedia: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  ProgressReportWorkflowEvent: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  ProjectChangeRequest: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  ProjectCreated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  ProjectDeleted: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  ProjectMember: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      modifiedAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  ProjectMemberCreated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  ProjectMemberDeleted: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  ProjectMemberUpdate: {
    fields: {
      inactiveAt: {
        read: optional(Parsers.DateTime),
      },
    },
  },
  ProjectMemberUpdated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  ProjectTransitioned: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  ProjectUpdate: {
    fields: {
      estimatedSubmission: {
        read: optional(Parsers.Date),
      },
      financialReportReceivedAt: {
        read: optional(Parsers.DateTime),
      },
      mouEnd: {
        read: optional(Parsers.Date),
      },
      mouStart: {
        read: optional(Parsers.Date),
      },
    },
  },
  ProjectUpdated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  ProjectWorkflowEvent: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  Prompt: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  PromptResponse: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  PromptVariantResponse: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      modifiedAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  Queue: {
    fields: {
      jobCounts: {
        read: Parsers.JSONObject,
      },
      metricsCompleted: {
        read: Parsers.JSONObject,
      },
      metricsFailed: {
        read: Parsers.JSONObject,
      },
    },
  },
  QueueJob: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      data: {
        read: optional(Parsers.JSON),
      },
      delayISO: {
        read: Parsers.Duration,
      },
      finishedAt: {
        read: optional(Parsers.DateTime),
      },
      json: {
        read: optional(Parsers.JSON),
      },
      options: {
        read: optional(Parsers.JSON),
      },
      processedAt: {
        read: optional(Parsers.DateTime),
      },
      progress: {
        read: optional(Parsers.JSON),
      },
      return: {
        read: optional(Parsers.JSON),
      },
    },
  },
  ResourceMutation: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
      changes: {
        read: optional(Parsers.JSONObject),
      },
    },
  },
  Rev79QuarterlyReportContextResult: {
    fields: {
      end: {
        read: Parsers.Date,
      },
      start: {
        read: Parsers.Date,
      },
    },
  },
  SecuredDate: {
    fields: {
      value: {
        read: optional(Parsers.Date),
      },
    },
  },
  SecuredDateNullable: {
    fields: {
      value: {
        read: optional(Parsers.Date),
      },
    },
  },
  SecuredDateTime: {
    fields: {
      value: {
        read: optional(Parsers.DateTime),
      },
    },
  },
  SecuredDateTimeNullable: {
    fields: {
      value: {
        read: optional(Parsers.DateTime),
      },
    },
  },
  SecuredRichText: {
    fields: {
      value: {
        read: optional(Parsers.RichText),
      },
    },
  },
  SecuredRichTextNullable: {
    fields: {
      value: {
        read: optional(Parsers.RichText),
      },
    },
  },
  Story: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  SystemNotification: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      message: {
        read: Parsers.Markdown,
      },
      readAt: {
        read: optional(Parsers.DateTime),
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  Tool: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      filteredUsages: pageLimitPagination({ sort: 'createdAt', order: 'ASC' }),
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
      usages: pageLimitPagination(),
    },
  },
  ToolUsage: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  Unavailability: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      history: pageLimitPagination(),
      tools: pageLimitPagination(),
    },
  },
  UserCreated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  UserDeleted: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  UserUpdated: {
    fields: {
      at: {
        read: Parsers.DateTime,
      },
    },
  },
  VariantResponse: {
    fields: {
      modifiedAt: {
        read: optional(Parsers.DateTime),
      },
    },
  },
  Video: {
    fields: {
      url: {
        read: Parsers.URL,
      },
    },
  },
  Webhook: {
    fields: {
      createdAt: {
        read: Parsers.DateTime,
      },
      metadata: {
        read: optional(Parsers.JSONObject),
      },
      modifiedAt: {
        read: Parsers.DateTime,
      },
      subscription: {
        read: Parsers.GraphQLDocument,
      },
      url: {
        read: Parsers.URL,
      },
      variables: {
        read: optional(Parsers.JSONObject),
      },
    },
  },
  Mutation: {
    fields: {
      deleteComment: handleDelete('Comment'),
      deleteEducation: handleDelete('Education'),
      deleteEngagement: handleDelete('Engagement'),
      deleteEthnoArt: handleDelete('EthnoArt'),
      deleteFieldRegion: handleDelete('FieldRegion'),
      deleteFieldZone: handleDelete('FieldZone'),
      deleteFileNode: handleDelete('FileNode'),
      deleteFilm: handleDelete('Film'),
      deleteFundingAccount: handleDelete('FundingAccount'),
      deleteLanguage: handleDelete('Language'),
      deleteLocation: handleDelete('Location'),
      deleteOrganization: handleDelete('Organization'),
      deletePartner: handleDelete('Partner'),
      deletePartnership: handleDelete('Partnership'),
      deletePost: handleDelete('Post'),
      deleteProduct: handleDelete('Product'),
      deleteProgressReportMedia: handleDelete('ProgressReportMedia'),
      deleteProject: handleDelete('Project'),
      deleteProjectChangeRequest: handleDelete('ProjectChangeRequest'),
      deleteProjectMember: handleDelete('ProjectMember'),
      deleteStory: handleDelete('Story'),
      deleteTool: handleDelete('Tool'),
      deleteToolUsage: handleDelete('ToolUsage'),
      deleteUnavailability: handleDelete('Unavailability'),
      deleteUser: handleDelete('User'),
      deleteWebhook: handleDelete('Webhook'),
    },
  },
};
